import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { Eye, Hand, Compass, ArrowRight, Check, Clock, FileText, Printer, Users, Play, Pause, Volume2, ChevronLeft, ChevronRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import AdinkraMark from "@/components/AdinkraMark";
import TruthJourney from "@/components/TruthJourney";
import LeakDiagnostic from "@/components/LeakDiagnostic";
import { TRAILER } from "@/config/media";

const H = "/api/htt";
const INVITATION = "/api/htt/media/narration/invitation.mp3";
const CARDS = [
  { img: "cards/card1-the-sentence.png", n: "01", t: "The sentence" },
  { img: "cards/card2-the-training.png", n: "02", t: "The training" },
  { img: "cards/card3-quiet-ward.png", n: "03", t: "The quiet ward" },
  { img: "cards/card4-thirty-seconds.png", n: "04", t: "Thirty seconds" },
  { img: "cards/card5-absent.png", n: "05", t: "Absent" },
  { img: "cards/card6-weather.png", n: "06", t: "The weather" },
  { img: "cards/card7-witness.png", n: "07", t: "The witness" },
];

// Warm the spectrum from cool indigo -> gold as the truth travels (start -> witness)
const CARD_GRADS = [
  "linear-gradient(145deg, rgba(88,55,170,0.62), rgba(34,20,82,0.28))",
  "linear-gradient(145deg, rgba(104,64,188,0.62), rgba(41,24,96,0.28))",
  "linear-gradient(145deg, rgba(128,70,196,0.60), rgba(48,27,114,0.28))",
  "linear-gradient(145deg, rgba(150,78,190,0.58), rgba(58,31,125,0.30))",
  "linear-gradient(145deg, rgba(176,92,186,0.56), rgba(70,37,132,0.30))",
  "linear-gradient(145deg, rgba(190,110,202,0.54), rgba(80,44,150,0.32))",
  "linear-gradient(145deg, rgba(168,96,206,0.56), rgba(74,38,150,0.36))",
];

function AudioMoment() {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) {
      a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };
  return (
    <div className="flex items-center gap-5 bg-purple-deep/40 border border-white/15 rounded-2xl p-5 backdrop-blur-md max-w-xl" data-testid="audio-moment">
      <button
        onClick={toggle}
        className="shrink-0 w-14 h-14 rounded-full bg-gold text-purple-deep flex items-center justify-center hover:bg-gold-pale transition-colors"
        data-testid="audio-play-btn"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
      </button>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.22em] text-gold-pale font-bold flex items-center gap-2">
          <Volume2 className="w-3.5 h-3.5" /> Hear the first moment
        </p>
        <p className="text-white/85 text-[15px] italic mt-1 leading-snug">
          “Someone tells you something. End of shift, the corridor, a colleague stops beside you…”
        </p>
      </div>
      <audio ref={ref} src={`${H}/rooms/assets/audio/m1-s1.mp3`} onEnded={() => setPlaying(false)} preload="none" />
    </div>
  );
}

// A 40s spoken invitation (ElevenLabs) that welcomes the visitor into the journey.
function InvitationMoment() {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };
  return (
    <button
      onClick={toggle}
      className="group inline-flex items-center gap-3 rounded-full border border-gold/40 bg-purple-deep/30 backdrop-blur-md pl-2 pr-5 py-2 hover:border-gold/70 transition-colors"
      data-testid="invitation-play-btn"
      aria-label={playing ? "Pause the invitation" : "Play the invitation"}
    >
      <span className="relative shrink-0 w-9 h-9 rounded-full bg-gold text-purple-deep flex items-center justify-center group-hover:bg-gold-pale transition-colors">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        {playing && <span className="htt-halo absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.45), transparent 70%)" }} />}
      </span>
      <span className="text-left">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-gold-pale font-bold">A spoken invitation · 40s</span>
        <span className="block text-white/80 text-[13px] leading-snug">{playing ? "Listening…" : "Hear why this journey matters"}</span>
      </span>
      <audio ref={ref} src={INVITATION} onEnded={() => setPlaying(false)} preload="none" />
    </button>
  );
}

function TrailerPlayer() {
  const vref = useRef(null);
  const [started, setStarted] = useState(false);
  const start = () => {
    const v = vref.current;
    if (!v) return;
    setStarted(true);
    v.play();
  };
  return (
    <div className="relative rounded-2xl overflow-hidden ring-1 ring-gold/30 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
      <video
        ref={vref}
        src={TRAILER}
        poster={HERO}
        controls={started}
        playsInline
        preload="none"
        onPause={() => {}}
        className="w-full aspect-video bg-black"
        data-testid="trailer-video"
      />
      {!started && (
        <button
          onClick={start}
          className="absolute inset-0 flex flex-col items-center justify-center group"
          data-testid="trailer-play-btn"
          aria-label="Play the film"
        >
          <span className="absolute inset-0 bg-purple-deep/45 group-hover:bg-purple-deep/35 transition-colors" />
          <span className="relative w-20 h-20 rounded-full bg-gold text-purple-deep flex items-center justify-center shadow-[0_0_50px_rgba(212,175,55,0.5)] group-hover:scale-110 transition-transform">
            <Play className="w-8 h-8 ml-1" fill="currentColor" />
          </span>
          <span className="relative mt-4 text-[11px] uppercase tracking-[0.22em] text-white font-bold">Play the film · 42s</span>
        </button>
      )}
    </div>
  );
}

const HERO = "/brand/oxytocin-hero.jpg";
const CLINICAL = "/textures/candlelit-lg.jpg";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div
      variants={fade}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Faded painterly watermark (from the brand's own artwork) sitting behind a section.
function Watermark({ src, position = "right", opacity = 0.06 }) {
  const pos = position === "left" ? "object-left" : position === "center" ? "object-center" : "object-right";
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <img src={src} alt="" className={`w-full h-full object-cover ${pos}`} style={{ opacity }} />
    </div>
  );
}

const MOVEMENTS = [
  { tag: "Perceive", icon: Eye, sym: "nteasee", mods: "Modules 1–3", body: "Learning to see what is actually happening — the thirty seconds, the four leaks, interpretation as a craft." },
  { tag: "Intervene", icon: Hand, sym: "nsaa", mods: "Modules 4–5", body: "Acting where you already have permission — naming the verb not the villain, closing the loop even when the answer is no." },
  { tag: "Redesign", icon: Compass, sym: "sankofa", mods: "Modules 6–8", body: "Changing what gets recorded, bought and built — your evidence, being in the room, your practice picture." },
];

const INSIDE = [
  { icon: FileText, t: "Eight interactive worksheet modules", d: "Type your answers straight onto the page — everything saves privately to your own account." },
  { icon: Users, t: "An expanded workbook for every module", d: "Context, worked examples, and a week of practice for each of the eight." },
  { icon: Printer, t: "The First Thirty Seconds pocket guide", d: "Print it, fold it, keep it in a pocket on shift." },
  { icon: Clock, t: "Around three hours of CPD-aligned learning", d: "Taken at your own pace, on any device, with progress tracked across the course." },
];

export default function Landing() {
  const lenisRef = useRef(null);
  const galleryRef = useRef(null);
  const scrollCards = (dir) => {
    if (galleryRef.current) galleryRef.current.scrollBy({ left: dir * 330, behavior: "smooth" });
  };
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="bg-cream overflow-hidden">
      <SiteNav variant="dark" />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end sm:items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-deep via-purple-deep/55 to-purple-deep/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-deep/80 via-transparent to-transparent" />
          <div className="absolute inset-0 velvet opacity-25" />
        </div>
        <AdinkraMark
          symbol="nkyinkyim"
          className="hidden lg:block absolute z-[1] text-gold pointer-events-none"
          style={{ width: "460px", height: "460px", right: "4%", top: "48%", transform: "translateY(-50%)", opacity: 0.08 }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-16 sm:py-32 w-full">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.28em] text-gold-pale font-bold mb-6"
          >
            A course in receiving difficult truth
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-white text-[clamp(2.6rem,7vw,5.2rem)] leading-[1.02] max-w-4xl text-balance"
          >
            How Truth Travels
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-7 max-w-2xl text-lg sm:text-xl text-white/85 leading-relaxed"
          >
            Somebody is going to tell you something difficult — probably this month. What you do in the first
            thirty seconds decides whether what has been entrusted to you begins to travel — or quietly disappears.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/join"
              className="group inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-8 py-4 text-base hover:bg-gold-pale transition-colors"
              data-testid="hero-join-btn"
            >
              Join the founding cohort — £39
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-white/70 text-sm max-w-[240px]">
              Founding price, rising to <b className="text-white">£99</b>. Coupon codes accepted at checkout.
            </p>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-10 text-[12px] uppercase tracking-[0.18em] text-gold-pale/90"
          >
            Eight modules · Three movements · Around three hours · CPD-aligned
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-8"
          >
            <AudioMoment />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-5"
          >
            <InvitationMoment />
          </motion.div>
        </div>
      </section>

      {/* CRAFT, NOT AWARENESS */}
      <section className="relative bg-cream py-24 sm:py-32 overflow-hidden">
        <Watermark src="/textures/journey.jpg" position="right" opacity={0.05} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-4">The premise</p>
              <h2 className="font-heading text-purple text-4xl sm:text-5xl leading-[1.08] max-w-xl text-balance">
                A craft course, not awareness training
              </h2>
            </Reveal>
            <Reveal i={1}>
              <p className="mt-8 text-lg text-ink/80 leading-relaxed max-w-2xl">
                You are already aware. This course is about what one practitioner can do — without budget, authority,
                or anybody's permission — when a difficult truth passes through their hands. You will leave with a
                small number of things you actually <span className="text-purple font-semibold">do differently</span>:
                a sentence, a question, a way of closing the loop. Not a folder of things you once agreed with.
              </p>
            </Reveal>
          </div>
          <Reveal i={2} className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden ring-1 ring-purple-deep/10 shadow-[0_20px_60px_rgba(34,20,82,0.16)]">
              <img src={CLINICAL} alt="A practitioner listening" className="w-full h-[360px] object-cover" />
              <div className="absolute inset-0 bg-purple-deep/10" />
            </div>
          </Reveal>
        </div>

        {/* Movements */}
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 mt-20 grid md:grid-cols-3 gap-6">
          {MOVEMENTS.map((m, i) => {
            const Icon = m.icon;
            return (
              <Reveal key={m.tag} i={i} className={i === 1 ? "md:translate-y-8" : ""}>
                <div className="relative overflow-hidden h-full bg-white rounded-2xl border border-line p-8 hover:-translate-y-2 hover:shadow-[0_18px_50px_rgba(34,20,82,0.12)] transition-[transform,box-shadow] duration-300">
                  <AdinkraMark
                    symbol={m.sym}
                    className="absolute text-gold pointer-events-none transition-transform duration-500 group-hover:scale-110"
                    style={{ width: "130px", height: "130px", right: "-22px", bottom: "-18px", opacity: 0.08 }}
                  />
                  <div className="relative z-10 w-12 h-12 rounded-xl bg-purple/8 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-purple-mid" strokeWidth={1.6} />
                  </div>
                  <p className="relative z-10 text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">{m.mods}</p>
                  <h3 className="relative z-10 font-heading text-2xl text-purple mt-1 mb-3">{m.tag}</h3>
                  <p className="relative z-10 text-[15px] text-ink/70 leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <LeakDiagnostic />

      <TruthJourney />

      {/* WHAT'S INSIDE */}
      <section className="relative bg-wash py-24 sm:py-28 overflow-hidden">
        <Watermark src="/textures/forward.jpg" position="left" opacity={0.05} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-4">What's inside</p>
            <h2 className="font-heading text-purple text-4xl sm:text-5xl max-w-xl leading-tight">Everything you need — nothing you'll skim</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {INSIDE.map((it, i) => {
              const Icon = it.icon;
              return (
                <Reveal key={it.t} i={i}>
                  <div className="flex gap-5 bg-cream rounded-2xl border border-line p-7 h-full">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gold/12 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gold-dark" strokeWidth={1.7} />
                    </div>
                    <div>
                      <h4 className="font-heading text-xl text-purple mb-1.5">{it.t}</h4>
                      <p className="text-[15px] text-ink/70 leading-relaxed">{it.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal i={2}>
            <p className="mt-8 text-[14px] text-lav border-l-2 border-gold pl-4 max-w-2xl">
              Nothing here asks you to disclose anything. The scenarios are fictional, nothing is timed, and there is no pass mark.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TRAILER */}
      <section className="relative bg-purple-deep velvet py-24 sm:py-28">
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-4">Watch the film</p>
              <h2 className="font-heading text-white text-4xl sm:text-5xl leading-tight">
                Before you practise, you watch
              </h2>
              <p className="mt-5 text-white/75 text-[16px]">
                Every module opens with a short film. You watch the moment unfold — then you step into the practice room and take it in your own hands.
              </p>
            </div>
          </Reveal>
          <Reveal i={1}>
            <TrailerPlayer />
          </Reveal>
        </div>
      </section>

      {/* STORY CARDS */}
      <section className="relative bg-cream py-24 sm:py-28 overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-4">The story in seven cards</p>
            <div className="flex items-end justify-between gap-6">
              <h2 className="font-heading text-purple text-4xl sm:text-5xl max-w-2xl leading-tight">
                A single disclosure, followed all the way through
              </h2>
              <div className="hidden sm:flex gap-3 shrink-0">
                <button onClick={() => scrollCards(-1)} aria-label="Previous cards" className="w-11 h-11 rounded-full border border-line bg-white text-purple hover:bg-wash transition-colors flex items-center justify-center" data-testid="cards-prev">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => scrollCards(1)} aria-label="Next cards" className="w-11 h-11 rounded-full border border-line bg-white text-purple hover:bg-wash transition-colors flex items-center justify-center" data-testid="cards-next">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="mt-5 text-lav text-[16px] max-w-2xl">
              The course moves like a story — from the sentence a colleague speaks, to the witness you become.
              Each card is a beat you'll practise with your own hands.
            </p>
          </Reveal>
        </div>
        <div ref={galleryRef} className="mt-12 flex gap-5 overflow-x-auto px-5 sm:px-8 pb-6 snap-x snap-mandatory scroll-pl-5 sm:scroll-pl-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-testid="story-cards">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="snap-start shrink-0 w-[200px] sm:w-[240px] group"
            >
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-purple-deep/10 shadow-[0_14px_40px_rgba(34,20,82,0.14)] bg-purple-deep transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_22px_60px_rgba(34,20,82,0.28)]">
                <img
                  src={`${H}/${c.img}`}
                  alt={c.t}
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = 0; }}
                />
                {/* per-card gradient wash — spectrum warms across the row */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: CARD_GRADS[i % CARD_GRADS.length] }} />
                {/* diagonal light sheen for depth */}
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 85% at 18% 0%, rgba(255,255,255,0.16), transparent 55%)" }} />
                {/* the finale ("The witness") catches the gold */}
                {i === CARDS.length - 1 && (
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 60% at 50% 6%, rgba(212,175,55,0.42), transparent 60%)" }} />
                )}
                {/* gold ring — always on for the finale, on hover for the rest */}
                <div className={`absolute inset-0 rounded-2xl ring-1 ring-inset transition-[box-shadow,border-color] duration-300 pointer-events-none ${i === CARDS.length - 1 ? "ring-gold/45" : "ring-gold/0"} group-hover:ring-gold/60`} />
                <div className="absolute inset-x-0 bottom-0 p-3.5 bg-gradient-to-t from-purple-deep/90 to-transparent">
                  <p className="text-[10px] tracking-[0.2em] text-gold-pale font-bold">{c.n}</p>
                  <p className="font-heading text-white text-[15px] leading-tight">{c.t}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      <section className="relative bg-purple-deep velvet py-28 sm:py-36">
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <AdinkraMark symbol="nyamedua" className="text-gold mx-auto mb-8 block" style={{ width: "44px", height: "44px", opacity: 0.9 }} />
            <div className="gold-rule w-16 mx-auto mb-10" />
            <p className="font-heading italic text-white text-[clamp(1.4rem,3.5vw,2.4rem)] leading-[1.35] text-balance">
              “Thank you for telling me. I'm going to stay with this — and I'll tell you what happens next.”
            </p>
            <p className="mt-8 text-[11px] uppercase tracking-[0.24em] text-gold-pale">The sentence · Module 1</p>
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative bg-cream py-24 sm:py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-white rounded-3xl border-2 border-gold p-9 shadow-[0_20px_60px_rgba(212,175,55,0.14)]">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Founding cohort</p>
              <p className="font-heading text-purple text-6xl mt-3">
                £39<span className="text-xl text-lav font-sans"> · rising to £99</span>
              </p>
              <p className="mt-4 text-[15px] text-ink/75 leading-relaxed">
                Full course access, every workbook and download included. Coupon codes are accepted at checkout.
              </p>
              <Link
                to="/join"
                className="mt-7 inline-flex items-center gap-2 bg-purple-deep text-cream font-bold rounded-full px-8 py-4 hover:bg-purple transition-colors"
                data-testid="pricing-join-btn"
              >
                Get access <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="mt-5 text-[13px] text-lav">
                Payment is handled securely by Stripe. Your access opens the moment payment completes.
              </p>
            </div>
          </Reveal>
          <Reveal i={1}>
            <div className="h-full bg-white rounded-3xl border border-line p-9">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">Where this goes next</p>
              <h3 className="font-heading text-2xl text-purple mt-2 mb-4">Phoenix Rising</h3>
              <p className="text-[15px] text-ink/75 leading-relaxed mb-3">
                How Truth Travels is the opening movement of the full programme — 62 CPD-aligned hours built on
                everything you practise here.
              </p>
              <p className="text-[15px] text-ink/75 leading-relaxed mb-3">
                The programme is £399, and as a founding member your £39 counts towards it in full:
                <span className="text-purple font-semibold"> £360 bridges you across.</span>
              </p>
              <p className="text-[15px] text-ink/75 leading-relaxed">
                And you don't walk it alone. The <b className="text-purple">Oxytocin House of Learning</b> is our free
                community — conversation, weekly live sessions, and colleagues walking the same path.
              </p>
              <ul className="mt-6 space-y-2.5">
                {["Every worksheet & expanded workbook", "The First Thirty Seconds pocket guide", "Your CPD certificate on completion", "A standing invitation to the community"].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-[14px] text-ink/75">
                    <Check className="w-4 h-4 text-gold-dark shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal i={2}>
          <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-10 text-center">
            <p className="text-lav text-sm">
              Already enrolled?{" "}
              <Link to="/login" className="text-purple-mid font-semibold hover:text-purple underline underline-offset-4" data-testid="pricing-signin-link">
                Sign in to continue the course →
              </Link>
            </p>
          </div>
        </Reveal>
      </section>

      <SiteFooter />
    </div>
  );
}
